import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PriceChart from './PriceChart';
import TradingViewChart from './TradingViewChart';
import ChartSymbolModal from './ChartSymbolModal';
import { parseFloatFromString } from '../utils/formatters';
import { TransactionType } from '../utils/enums';
import { filterVisibleChartExitLevels } from '../utils/exitLevels';
import { isEphemeralTokenId } from '../utils/tokenId';

// Chart type constants
const CHART_TYPES = {
  LIGHT: 'light',
  TRADINGVIEW: 'tradingview'
};

// TradingView pair mode
const PAIR_MODES = {
  CURRENCY: 'currency',
  BTC: 'btc'
};

// Markers Modal Component
const MarkersModal = ({ isOpen, onClose, settings, onSettingsChange }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 min-w-[280px]">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-gray-900 dark:text-white">Chart Markers</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="space-y-3">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <span className="w-3 h-3 rounded-full mr-2" style={{ background: 'linear-gradient(to right, #00e676 50%, #ff5252 50%)' }}></span>
              Buy/Sell Markers
            </span>
            <input
              type="checkbox"
              checked={settings.showBuySellMarkers}
              onChange={(e) => onSettingsChange({ ...settings, showBuySellMarkers: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <span className="w-3 h-3 rounded-full mr-2" style={{ background: '#ffb74d' }}></span>
              Average Buy Price
            </span>
            <input
              type="checkbox"
              checked={settings.showAvgBuyPrice}
              onChange={(e) => onSettingsChange({ ...settings, showAvgBuyPrice: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <span className="w-3 h-3 rounded-full mr-2" style={{ background: '#f06292' }}></span>
              Average Sell Price
            </span>
            <input
              type="checkbox"
              checked={settings.showAvgSellPrice}
              onChange={(e) => onSettingsChange({ ...settings, showAvgSellPrice: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <span className="w-3 h-3 rounded-full mr-2" style={{ background: '#A5D6A7' }}></span>
              Exit Levels
            </span>
            <input
              type="checkbox"
              checked={settings.showExitLevels}
              onChange={(e) => onSettingsChange({ ...settings, showExitLevels: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
              <span className="w-3 h-3 rounded-full mr-2" style={{ background: '#26A69A' }}></span>
              Risk Score
            </span>
            <input
              type="checkbox"
              checked={settings.showRiskScore}
              onChange={(e) => onSettingsChange({ ...settings, showRiskScore: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
        </div>
      </div>
    </div>
  );
};

// Time range controls removed for compact UI

const TokenPriceChart = ({ 
  tokenId, 
  tokenTicker,
  chartSymbol,
  averageBuyPrice,
  averageSellPrice,
  defaultTimeRange,
  transactions = [],
  plannedLevels = [],
  onChartSymbolUpdate,
  holdingsAmount,
  priceHistory = [],
  riskScoreHistory = [],
  portfolioCurrency = 'USD'
}) => {
  const location = useLocation();
  
  // Storage key for persisting settings
  const STORAGE_KEY = 'tokenChartToggles_v2';
  
  const getStoredSettings = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  };
  
  const stored = getStoredSettings();
  
  // Marker settings (for Light chart)
  const [markerSettings, setMarkerSettings] = useState({
    showBuySellMarkers: stored?.showBuySellMarkers ?? true,
    showAvgBuyPrice: stored?.showAvgBuyPrice ?? true,
    showAvgSellPrice: stored?.showAvgSellPrice ?? false,
    showExitLevels: stored?.showExitLevels ?? true,
    showRiskScore: stored?.showRiskScore ?? false,
  });
  
  // Chart type: light or tradingview
  const tokenIsEphemeral = isEphemeralTokenId(tokenId);
  const [chartType, setChartType] = useState(() => 
    tokenIsEphemeral ? CHART_TYPES.TRADINGVIEW : CHART_TYPES.LIGHT
  );
  
  // TradingView pair mode: currency or btc
  const [pairMode, setPairMode] = useState(stored?.pairMode ?? PAIR_MODES.CURRENCY);
  
  const [chartKey, setChartKey] = useState(Date.now());
  
  // Modals
  const [chartSymbolModal, setChartSymbolModal] = useState({ isOpen: false });
  const [markersModalOpen, setMarkersModalOpen] = useState(false);
  
  // Track previous ephemeral state to detect transitions
  const prevEphemeralRef = useRef(tokenIsEphemeral);
  
  // Switch to light chart when token transitions from ephemeral to persisted
  useEffect(() => {
    const wasEphemeral = prevEphemeralRef.current;
    prevEphemeralRef.current = tokenIsEphemeral;
    
    if (wasEphemeral && !tokenIsEphemeral && chartType === CHART_TYPES.TRADINGVIEW) {
      setChartType(CHART_TYPES.LIGHT);
    }
  }, [tokenIsEphemeral, chartType]);

  // Reset chart key when chart type changes or location changes
  useEffect(() => {
    setChartKey(Date.now());
  }, [chartType, pairMode, location.pathname, markerSettings]);

  // Persist settings globally across tokens
  useEffect(() => {
    try {
      const payload = {
        ...markerSettings,
        pairMode,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (_) {
      // ignore storage failures
    }
  }, [markerSettings, pairMode]);
  
  // Time range change removed

  // Open chart symbol modal
  const openChartSymbolModal = () => {
    setChartSymbolModal({ isOpen: true });
  };
  
  // Close chart symbol modal
  const closeChartSymbolModal = () => {
    setChartSymbolModal({ isOpen: false });
    // Notify parent to refetch data for updated chart symbol
    if (onChartSymbolUpdate) {
      onChartSymbolUpdate();
    }
  };

  // Use the price history from props
  const chartData = priceHistory || [];
  
  // Filter transactions to only include those within the selected time range
  const filterTransactionsByTimeRange = (transactions, chartData) => {
    if (!chartData || chartData.length === 0 || !transactions || transactions.length === 0) {
      return [];
    }
    
    // Get the earliest timestamp in the chart data
    const earliestTimestamp = chartData.length > 0 ? chartData[0].time * 1000 : 0; // Convert to milliseconds
    
    // Filter transactions to only include those within the time range
    return transactions.filter(tx => {
      if (!tx.timestamp) return false;
      
      // Convert transaction timestamp to milliseconds
      const txTime = new Date(tx.timestamp).getTime();
      
      // Include only transactions that occurred after the earliest time in the chart
      return txTime >= earliestTimestamp;
    });
  };

  // Filter transactions to only include buy and sell types within the current time range
  const chartTransactions = filterTransactionsByTimeRange(
    transactions.filter(tx => {
      // Normalize transaction type to always use uppercase enum format
      const txType = tx.transactionType?.toUpperCase?.() || tx.transactionType;
      return txType === TransactionType.BUY || txType === TransactionType.SELL;
    }),
    chartData
  );
  
  // Generate TradingView symbols based on pair mode and portfolio currency
  const getBaseSymbol = () => chartSymbol || `BINANCE:${tokenTicker}USDT`;
  
  const getTradingViewSymbol = () => {
    const baseSymbol = getBaseSymbol();
    
    if (pairMode === PAIR_MODES.BTC) {
      // TOKEN vs BTC: TOKEN_USD / BTC_USD
      // If portfolio currency is not USD, we still show vs BTC in USD terms
      return `${baseSymbol}/BINANCE:BTCUSDT`;
    }
    
    // TOKEN vs Portfolio Currency
    if (portfolioCurrency === 'USD' || portfolioCurrency === 'USDT') {
      return baseSymbol;
    }
    
    // For other currencies: TOKEN_USD / CURRENCY_USD to get TOKEN/CURRENCY
    // e.g., BTCUSDT / JPYUSD = BTC in JPY
    // Using FX_IDC exchange for forex pairs (FX: doesn't work for many pairs)
    const currencyPair = `FX_IDC:${portfolioCurrency}USD`;
    return `${baseSymbol}/${currencyPair}`;
  };
  
  // Render TradingView charts
  const renderTradingViewChart = () => {
    const symbol = getTradingViewSymbol();
    return (
      <TradingViewChart 
        key={`chart-tv-${symbol}-${chartKey}`}
        symbol={symbol} 
        theme="dark" 
        height="400px" 
        width="100%" 
      />
    );
  };
  
  const visibleExitLevels = filterVisibleChartExitLevels(plannedLevels);

  // Render Line Chart with markers
  const renderLineChart = () => {
    const { showBuySellMarkers, showAvgBuyPrice, showAvgSellPrice, showExitLevels, showRiskScore } = markerSettings;
    return (
      <div>
        <PriceChart 
          key={`chart-${chartKey}-${showBuySellMarkers ? 1 : 0}-${showAvgBuyPrice ? 1 : 0}-${showAvgSellPrice ? 1 : 0}-${showExitLevels ? 1 : 0}-${showRiskScore ? 1 : 0}`}
          data={chartData}
          averageBuyPrice={showAvgBuyPrice ? parseFloatFromString(averageBuyPrice) : null}
          averageSellPrice={showAvgSellPrice ? parseFloatFromString(averageSellPrice) : null}
          transactions={showBuySellMarkers ? chartTransactions : []}
          plannedLevels={showExitLevels ? visibleExitLevels : []}
          holdingsAmount={holdingsAmount}
          ticker={tokenTicker}
          riskScoreHistory={showRiskScore ? riskScoreHistory : []}
        />
      </div>
    );
  };
  
  // No data state
  if (chartData.length === 0 && chartType === CHART_TYPES.LIGHT) {
    return (
      <div className="w-full">
        <div className="border border-gray-700 rounded p-6 flex justify-center items-center h-96">
          <div className="text-gray-400">
            No price history data available for this token
          </div>
        </div>

        {/* Chart Controls: Left-aligned on small screens, centered on larger */}
        <div className="flex items-center justify-between mt-3">
          {/* Left spacer - hidden on small screens for left-aligned toggle */}
          <div className="hidden sm:flex sm:flex-1"></div>
          
          {/* Chart Type Toggle - left on small screens, centered on larger */}
          <div className="inline-flex rounded overflow-hidden border border-gray-300 dark:border-gray-600">
            <button
              title={tokenIsEphemeral ? "Light chart available after adding transaction" : "Light chart"}
              className={`px-3 py-1.5 text-xs font-medium ${
                chartType === CHART_TYPES.LIGHT
                  ? 'bg-blue-600 text-white'
                  : tokenIsEphemeral
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
              onClick={() => !tokenIsEphemeral && setChartType(CHART_TYPES.LIGHT)}
              disabled={tokenIsEphemeral}
            >
              Light
            </button>
            <button
              title="TradingView chart"
              className={`px-3 py-1.5 text-xs font-medium border-l border-gray-300 dark:border-gray-600 ${
                chartType === CHART_TYPES.TRADINGVIEW
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
              onClick={() => setChartType(CHART_TYPES.TRADINGVIEW)}
            >
              TradingView
            </button>
          </div>
          
          {/* Right-side controls */}
          <div className="flex-1 flex justify-end items-center space-x-2">
            {chartType === CHART_TYPES.LIGHT ? (
              <button
                onClick={() => setMarkersModalOpen(true)}
                className="px-2 py-1.5 text-xs rounded flex items-center bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Markers
              </button>
            ) : (
              <>
                {/* Pair mode toggle */}
                <div className="inline-flex rounded overflow-hidden border border-gray-300 dark:border-gray-600">
                  <button
                    title={`Show in ${portfolioCurrency}`}
                    className={`px-2 py-1 text-xs ${
                      pairMode === PAIR_MODES.CURRENCY
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                    onClick={() => setPairMode(PAIR_MODES.CURRENCY)}
                  >
                    {portfolioCurrency}
                  </button>
                  <button
                    title="Show vs BTC"
                    className={`px-2 py-1 text-xs border-l border-gray-300 dark:border-gray-600 ${
                      pairMode === PAIR_MODES.BTC
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                    onClick={() => setPairMode(PAIR_MODES.BTC)}
                  >
                    BTC
                  </button>
                </div>
                
                {/* Set Symbol button */}
                {!tokenIsEphemeral && (
                  <button
                    onClick={openChartSymbolModal}
                    className={`px-2 py-1.5 text-xs rounded flex items-center ${
                      chartSymbol
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800/50'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                    title={chartSymbol ? `Custom symbol: ${chartSymbol}` : "Set Symbol"}
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {chartSymbol ? chartSymbol : 'Symbol'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Markers Modal */}
        <MarkersModal
          isOpen={markersModalOpen}
          onClose={() => setMarkersModalOpen(false)}
          settings={markerSettings}
          onSettingsChange={setMarkerSettings}
        />
      </div>
    );
  }
  
  return (
    <div className="w-full">
      {/* Chart Content */}
      <div>
        {chartType === CHART_TYPES.LIGHT ? renderLineChart() : renderTradingViewChart()}
      </div>

      {/* Chart Controls: Left-aligned on small screens, centered on larger */}
      <div className="flex items-center justify-between mt-3">
        {/* Left spacer - hidden on small screens for left-aligned toggle */}
        <div className="hidden sm:flex sm:flex-1"></div>
        
        {/* Chart Type Toggle - left on small screens, centered on larger */}
        <div className="inline-flex rounded overflow-hidden border border-gray-300 dark:border-gray-600">
          <button
            title={tokenIsEphemeral ? "Light chart available after adding transaction" : "Light chart"}
            className={`px-3 py-1.5 text-xs font-medium ${
              chartType === CHART_TYPES.LIGHT
                ? 'bg-blue-600 text-white'
                : tokenIsEphemeral
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            onClick={() => !tokenIsEphemeral && setChartType(CHART_TYPES.LIGHT)}
            disabled={tokenIsEphemeral}
          >
            Light
          </button>
          <button
            title="TradingView chart"
            className={`px-3 py-1.5 text-xs font-medium border-l border-gray-300 dark:border-gray-600 ${
              chartType === CHART_TYPES.TRADINGVIEW
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
            onClick={() => setChartType(CHART_TYPES.TRADINGVIEW)}
          >
            TradingView
          </button>
        </div>
        
        {/* Right-side controls */}
        <div className="flex-1 flex justify-end items-center space-x-2">
          {chartType === CHART_TYPES.LIGHT ? (
            <button
              onClick={() => setMarkersModalOpen(true)}
              className="px-2 py-1.5 text-xs rounded flex items-center bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Markers
            </button>
          ) : (
            <>
              {/* Pair mode toggle */}
              <div className="inline-flex rounded overflow-hidden border border-gray-300 dark:border-gray-600">
                <button
                  title={`Show in ${portfolioCurrency}`}
                  className={`px-2 py-1 text-xs ${
                    pairMode === PAIR_MODES.CURRENCY
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setPairMode(PAIR_MODES.CURRENCY)}
                >
                  {portfolioCurrency}
                </button>
                <button
                  title="Show vs BTC"
                  className={`px-2 py-1 text-xs border-l border-gray-300 dark:border-gray-600 ${
                    pairMode === PAIR_MODES.BTC
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setPairMode(PAIR_MODES.BTC)}
                >
                  BTC
                </button>
              </div>
              
              {/* Set Symbol button */}
              {!tokenIsEphemeral && (
                <button
                  onClick={openChartSymbolModal}
                  className={`px-2 py-1.5 text-xs rounded flex items-center ${
                    chartSymbol
                      ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800/50'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                  title={chartSymbol ? `Custom symbol: ${chartSymbol}` : "Set Symbol"}
                >
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {chartSymbol ? chartSymbol : 'Symbol'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Markers Modal */}
      <MarkersModal
        isOpen={markersModalOpen}
        onClose={() => setMarkersModalOpen(false)}
        settings={markerSettings}
        onSettingsChange={setMarkerSettings}
      />

      {/* Chart Symbol Modal */}
      {chartSymbolModal.isOpen && !tokenIsEphemeral && (
        <ChartSymbolModal 
          isOpen={chartSymbolModal.isOpen} 
          onClose={closeChartSymbolModal} 
          token={{ id: tokenId, ticker: tokenTicker, chartSymbol }}
        />
      )}
    </div>
  );
};

export default TokenPriceChart; 